import { Fragment, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import type { CaseOverride, RestaurantSummary } from '../api/schemas';
import { buildMapUrl } from '../lib/mapUrl';
import { toDisplayCase } from '../lib/displayCase';
import { GradeBadge } from './GradeBadge';

export type SortKey =
  | 'restaurant_name'
  | 'address'
  | 'current_grade'
  | 'date_current';

export type SortDir = 'asc' | 'desc';

interface Props {
  results: RestaurantSummary[];
  sort: SortKey;
  dir: SortDir;
  onSort: (key: SortKey) => void;
  onSelect: (permitNumber: string) => void;
  selectedPermit: string | null;
  isLoading: boolean;
  /** Inline expansion content rendered in a row directly beneath the selected establishment. */
  renderDetail?: (permitNumber: string) => ReactNode;
  /** DB-sourced case overrides from the lookups endpoint. */
  overrides?: CaseOverride[];
}

const COLUMNS: Array<{ key: SortKey | null; label: string; align?: 'left' | 'center' }> = [
  { key: 'restaurant_name', label: 'Establishment Name' },
  { key: 'address', label: 'Address' },
  { key: 'current_grade', label: 'Current Grade', align: 'center' },
  { key: null, label: 'Number of Demerits', align: 'center' },
  { key: 'date_current', label: 'Last Inspection' },
  { key: null, label: 'Action' },
];

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s.replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString();
}

export function ResultsTable({
  results,
  sort,
  dir,
  onSort,
  onSelect,
  selectedPermit,
  isLoading,
  renderDetail,
  overrides = [],
}: Props) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {COLUMNS.map((col) => {
              const sortable = col.key !== null;
              const active = sortable && sort === col.key;
              const Icon = !active
                ? ChevronsUpDown
                : dir === 'asc'
                  ? ChevronUp
                  : ChevronDown;
              return (
                <th
                  key={col.label}
                  scope="col"
                  className={`px-4 py-3 text-${col.align ?? 'left'} text-xs font-medium text-gray-700 uppercase tracking-wider ${
                    sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                  }`}
                  onClick={sortable ? () => onSort(col.key as SortKey) : undefined}
                  aria-sort={
                    !sortable
                      ? undefined
                      : active
                        ? dir === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                  }
                >
                  <div
                    className={`flex items-center space-x-1 ${col.align === 'center' ? 'justify-center' : ''}`}
                  >
                    <span>{col.label}</span>
                    {sortable && (
                      <Icon
                        className={`w-4 h-4 ${active ? 'text-snhd-primary' : 'text-gray-400'}`}
                        aria-hidden
                      />
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {isLoading && results.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-sm text-gray-500">
                Loading…
              </td>
            </tr>
          ) : results.length === 0 ? (
            <tr>
              <td colSpan={COLUMNS.length} className="px-4 py-12 text-center text-sm text-gray-500">
                No results. Try fewer filters.
              </td>
            </tr>
          ) : (
            results.map((r) => {
              const selected = selectedPermit === r.permit_number;
              return (
                <Fragment key={r.permit_number}>
                  <tr
                    onClick={() => onSelect(r.permit_number)}
                    className={`cursor-pointer transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {toDisplayCase(r.restaurant_name, overrides, 'name')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {toDisplayCase(r.address, overrides, 'address')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <GradeBadge code={r.current_grade} />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <span
                        className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                          (r.current_demerits ?? 0) === 0
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                        aria-label={`${r.current_demerits ?? 0} demerits`}
                      >
                        {r.current_demerits ?? 0}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                      {fmtDate(r.date_current)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-3 text-sm font-medium">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelect(r.permit_number);
                          }}
                          className="text-snhd-primary hover:text-snhd-primary-hover"
                        >
                          {selected ? 'Hide' : 'Details'}
                        </button>
                        <a
                          href={buildMapUrl(r)}
                          target="_blank"
                          rel="noreferrer noopener"
                          onClick={(e) => e.stopPropagation()}
                          className="text-snhd-primary hover:text-snhd-primary-hover"
                        >
                          Map
                        </a>
                      </div>
                    </td>
                  </tr>
                  {selected && renderDetail && (
                    <tr className="bg-gray-50">
                      <td colSpan={COLUMNS.length} className="p-0">
                        {renderDetail(r.permit_number)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}
